import React, { useState, useEffect } from "react";
import adminProductService from "../../db/admin-product-service";
import { Typography } from "@mui/material";
import * as XLSX from 'xlsx';
import { migrateProductContent } from "../../utils/migrateProductContent";
import { bulkUpdateProductsWithCardFields } from "../../utils/bulkUpdateProducts";
import { getGitHubImageUrl, generateImagePath } from "../../utils/githubImageUrl";
import { normalizeGitHubUrl } from "../../utils/normalizeGitHubUrl";
import "./AdminProductList.scss";

const ProductList = (props) => {
  const [products, setProducts] = useState([]);
  const [productName, setProductName] = useState("");
  const [productDesc, setProductDesc] = useState("");
  const [productImage, setProductImage] = useState(null);
  const [isOrganic, setIsOrganic] = useState(false);
  const [unit, setUnit] = useState("kg");
  const [category, setCategory] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(true);
  const [migrating, setMigrating] = useState(false); // Changed to true to show inactive products by default
  
  // Detailed product information
  const [productHighlights, setProductHighlights] = useState("");
  const [appearance, setAppearance] = useState("");
  const [benefits, setBenefits] = useState("");
  const [shelfLife, setShelfLife] = useState("");
  const [countryOfOrigin, setCountryOfOrigin] = useState("India");
  const [brandName, setBrandName] = useState("Organic");
  const [disclaimer, setDisclaimer] = useState("");
  const [cardBenefit, setCardBenefit] = useState("");
  const [cardPackageInfo, setCardPackageInfo] = useState("");
  const [cardKeyFeatures, setCardKeyFeatures] = useState("");
  const [ingredients, setIngredients] = useState("");
  const [usageInstructions, setUsageInstructions] = useState("");
  const [storageInstructions, setStorageInstructions] = useState("");
  const [certifications, setCertifications] = useState("");
  const [highlights, setHighlights] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [galleryImages, setGalleryImages] = useState(["", "", "", ""]);
  
  // Country-specific pricing
  const [pricingUSD, setPricingUSD] = useState(0);
  const [pricingINR, setPricingINR] = useState(0);
  const [pricingGBP, setPricingGBP] = useState(0);
  const [pricingEUR, setPricingEUR] = useState(0);
  const [pricingJPY, setPricingJPY] = useState(0);
  
  // Country-specific stock
  const [stockUS, setStockUS] = useState(0);
  const [stockIN, setStockIN] = useState(0);
  const [stockGB, setStockGB] = useState(0);
  const [stockEU, setStockEU] = useState(0);
  const [stockJP, setStockJP] = useState(0);
  
  // Country availability
  const [availability, setAvailability] = useState(['US', 'IN', 'GB', 'EU', 'JP']);

  // Currency symbols
  const currencySymbols = {
    'US': '$',
    'IN': '₹',
    'GB': '£',
    'EU': '€',
    'JP': '¥'
  };

  const handleCategoryChange = (categoryValue, isChecked) => {
    if (isChecked) {
      if (!category.includes(categoryValue)) {
        setCategory([...category, categoryValue]);
      }
    } else {
      setCategory(category.filter(c => c !== categoryValue));
    }
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imagePath = generateImagePath('new-product', 'main');
      setProductImage({ file, path: imagePath });
    }
  };

  // Handle Excel file selection
  const handleExcelChange = (e) => {
    setExcelFile(e.target.files[0]);
  };

  // Import products from Excel
  const importFromExcel = async () => {
    if (!excelFile) {
      alert("Please select an Excel file first.");
      return;
    }

    setImporting(true);
    try {
      const data = await excelFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      let successCount = 0;
      let errorCount = 0;

      for (const row of jsonData) {
        try {
          const availability = row.availability ? row.availability.split(',').map(c => c.trim()) : ['US', 'IN', 'GB', 'EU', 'JP'];
          
          const productData = {
            name: row.name || '',
            description: row.description || '',
            unit: row.unit || 'kg',
            is_organic: row.is_organic ? 1 : 0,
            pricing: {
              USD: parseFloat(row.pricing_USD) || 0,
              INR: parseFloat(row.pricing_INR) || 0,
              GBP: parseFloat(row.pricing_GBP) || 0,
              EUR: parseFloat(row.pricing_EUR) || 0,
              JPY: parseFloat(row.pricing_JPY) || 0
            },
            countryStock: {
              US: parseInt(row.stock_US) || 0,
              IN: parseInt(row.stock_IN) || 0,
              GB: parseInt(row.stock_GB) || 0,
              EU: parseInt(row.stock_EU) || 0,
              JP: parseInt(row.stock_JP) || 0
            },
            image_url: row.image_url || "https://via.placeholder.com/200?text=" + encodeURIComponent(row.name || 'Product'),
            availability: availability,
            active: true
          };

          await adminProductService.addProduct(productData);
          successCount++;
        } catch (err) {
          console.error('Error importing product:', row.name, err);
          errorCount++;
        }
      }

      alert(`Import completed! ${successCount} products added successfully. ${errorCount} errors.`);
      setExcelFile(null);
      fetchProducts();
    } catch (err) {
      console.error('Error reading Excel file:', err);
      alert('Failed to read Excel file: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  // Handle country availability toggle
  const handleAvailabilityChange = (country, isChecked) => {
    if (isChecked) {
      if (!availability.includes(country)) {
        setAvailability([...availability, country]);
      }
    } else {
      setAvailability(availability.filter(c => c !== country));
    }
  };

  const handleMigrateContent = async () => {
    if (!window.confirm('This will update all existing products with card fields. Continue?')) {
      return;
    }
    setMigrating(true);
    try {
      const result = await bulkUpdateProductsWithCardFields();
      alert(`Migration complete!\nUpdated: ${result.updatedCount}\nErrors: ${result.errorCount}`);
      fetchProducts();
    } catch (err) {
      alert('Migration failed: ' + err.message);
    } finally {
      setMigrating(false);
    }
  };

  const addProduct = async () => {
    if (!productName || !productDesc) {
      alert("Please fill all required fields correctly.");
      return;
    }

    try {
      // Log the availability to debug
      console.log("Adding product with availability:", availability);
      
      // Use local image URL
      const imageUrl = productImage?.path || "https://via.placeholder.com/200?text=" + encodeURIComponent(productName);
      const normalizedGallery = galleryImages.filter(img => img && img.trim());
      
      // Create pricing object with only selected countries
      const pricing = {};
      if (availability.includes('US')) {
        const usdPrice = parseFloat(pricingUSD);
        if (usdPrice > 0) pricing.USD = usdPrice;
      }
      if (availability.includes('IN')) {
        const inrPrice = parseFloat(pricingINR);
        if (inrPrice > 0) pricing.INR = inrPrice;
      }
      if (availability.includes('GB')) {
        const gbpPrice = parseFloat(pricingGBP);
        if (gbpPrice > 0) pricing.GBP = gbpPrice;
      }
      if (availability.includes('EU')) {
        const eurPrice = parseFloat(pricingEUR);
        if (eurPrice > 0) pricing.EUR = eurPrice;
      }
      if (availability.includes('JP')) {
        const jpyPrice = parseFloat(pricingJPY);
        if (jpyPrice > 0) pricing.JPY = jpyPrice;
      }
      
      // Check if at least one price is set for available countries
      if (Object.keys(pricing).length === 0) {
        alert("Please set at least one price for selected countries.");
        return;
      }
      
      // Create stock object with only selected countries
      const countryStock = {};
      if (availability.includes('US')) {
        const usStock = parseInt(stockUS);
        if (usStock >= 0) countryStock.US = usStock;
      }
      if (availability.includes('IN')) {
        const inStock = parseInt(stockIN);
        if (inStock >= 0) countryStock.IN = inStock;
      }
      if (availability.includes('GB')) {
        const gbStock = parseInt(stockGB);
        if (gbStock >= 0) countryStock.GB = gbStock;
      }
      if (availability.includes('EU')) {
        const euStock = parseInt(stockEU);
        if (euStock >= 0) countryStock.EU = euStock;
      }
      if (availability.includes('JP')) {
        const jpStock = parseInt(stockJP);
        if (jpStock >= 0) countryStock.JP = jpStock;
      }
      
      // Create product data
      const productData = {
        name: productName,
        description: productDesc,
        pricing: pricing,
        countryStock: countryStock,
        is_organic: isOrganic ? 1 : 0,
        image_url: imageUrl,
        availability: availability,
        active: true,
        unit: unit,
        category: category,
        cardBenefit: cardBenefit,
        cardPackageInfo: cardPackageInfo,
        cardKeyFeatures: cardKeyFeatures,
        highlights: highlights,
        ingredients: ingredients,
        usageInstructions: usageInstructions,
        storageInstructions: storageInstructions,
        certifications: certifications,
        videoUrl: videoUrl,
        galleryImages: galleryImages.filter(img => img && img.trim()),
        productHighlights: productHighlights,
        appearance: appearance,
        benefits: benefits,
        shelfLife: shelfLife,
        countryOfOrigin: countryOfOrigin,
        brandName: brandName,
        disclaimer: disclaimer
      };
      
      console.log("DEBUG: Full product data:", JSON.stringify(productData, null, 2));
      
      console.log("Sending product data:", JSON.stringify(productData, null, 2));
      
      // Use PouchDB admin product service
      const result = await adminProductService.addProduct(productData);
      console.log("Add product result:", result);
      
      alert("Product added successfully!");
      resetForm();
      fetchProducts();
    } catch (err) {
      console.error("Error adding product", err);
      alert("Failed to add product: " + err.message);
    }
  };

  const resetForm = () => {
    setProductName("");
    setPricingUSD(0);
    setPricingINR(0);
    setPricingGBP(0);
    setPricingEUR(0);
    setPricingJPY(0);
    setStockUS(0);
    setStockIN(0);
    setStockGB(0);
    setStockEU(0);
    setStockJP(0);
    setProductDesc("");
    setProductImage(null);
    setIsOrganic(false);
    setUnit("kg");
    setCategory([]);
    setAvailability(['US', 'IN', 'GB', 'EU', 'JP']);
    setCardBenefit("");
    setCardPackageInfo("");
    setCardKeyFeatures("");
    setHighlights("");
    setIngredients("");
    setUsageInstructions("");
    setStorageInstructions("");
    setCertifications("");
    setProductHighlights("");
    setAppearance("");
    setBenefits("");
    setShelfLife("");
    setCountryOfOrigin("India");
    setBrandName("Organic");
    setDisclaimer("");
    setVideoUrl("");
    setGalleryImages(["", "", "", ""]);
  };

  useEffect(() => {
    fetchProducts();
  }, [showInactive]);

  // Fetch product list
  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use PouchDB admin product service
      const productList = await adminProductService.getAllProducts(true); // Get all products including inactive
      console.log("Products fetched:", productList);
      
      // No need to filter by active status since we want to show all products
      setProducts(productList || []);
    } catch (err) {
      console.log("Couldn't fetch product list", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Open product details
  const openProductDetails = (product) => {
    console.log("Opening product details for:", product);
    props.handleProductDetails(product);
  };

  // Delete product (mark as inactive)
  const deleteProduct = async (productId) => {
    try {
      // Use PouchDB admin product service
      await adminProductService.deleteProduct(productId);
      alert("Product marked as inactive!");
      fetchProducts();
    } catch (err) {
      console.log("Error marking product as inactive", err);
      alert("Error: " + err.message);
    }
  };

  // Restore product (mark as active)
  const restoreProduct = async (productId) => {
    try {
      // Use PouchDB admin product service
      await adminProductService.setProductActive(productId, true);
      alert("Product restored to active status!");
      fetchProducts();
    } catch (err) {
      console.log("Error restoring product", err);
      alert("Error: " + err.message);
    }
  };

  // Format price with currency symbol
  const formatPrice = (price, countryCode) => {
    if (!price) return "-";
    return `${currencySymbols[countryCode]}${parseFloat(price).toFixed(2)}`;
  };

  // Get stock for a specific country
  const getCountryStock = (product, country) => {
    if (product.countryStock && product.countryStock[country] !== undefined) {
      return product.countryStock[country];
    }
    return 0;
  };

  // Get availability status for a country
  const isAvailableIn = (product, country) => {
    if (!product.availability) return true;
    return product.availability.includes(country);
  };

  if (loading) {
    return <div>Loading products...</div>;
  }

  return (
    <div className="product-list-container">
      <div className="add-product-section">
        <h2>Add New Product</h2>
        
        <div className="form-group">
          <label htmlFor="productName">Product Name:</label>
          <input
            type="text"
            id="productName"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="Product Name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="unit">Unit:</label>
          <select
            id="unit"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          >
            <option value="kg">kg</option>
            <option value="grams">gram</option>
            <option value="liters">liter</option>
            <option value="ml">ml</option>
            <option value="shot">shot</option>
            <option value="unit">unit</option>
            <option value="each">each</option>
          </select>
        </div>

        <div className="form-group">
          <label>Categories:</label>
          <div className="category-checkboxes">
            <label>
              <input
                type="checkbox"
                checked={category.includes('metabolic')}
                onChange={(e) => handleCategoryChange('metabolic', e.target.checked)}
              />
              Blood Sugar Support
            </label>
            <label>
              <input
                type="checkbox"
                checked={category.includes('cardiovascular')}
                onChange={(e) => handleCategoryChange('cardiovascular', e.target.checked)}
              />
              Heart Health
            </label>
            <label>
              <input
                type="checkbox"
                checked={category.includes('immunity')}
                onChange={(e) => handleCategoryChange('immunity', e.target.checked)}
              />
              Immunity Boost
            </label>
            <label>
              <input
                type="checkbox"
                checked={category.includes('digestive')}
                onChange={(e) => handleCategoryChange('digestive', e.target.checked)}
              />
              Gut Health
            </label>
            <label>
              <input
                type="checkbox"
                checked={category.includes('detox')}
                onChange={(e) => handleCategoryChange('detox', e.target.checked)}
              />
              Detox & Repair
            </label>
            <label>
              <input
                type="checkbox"
                checked={category.includes('premium')}
                onChange={(e) => handleCategoryChange('premium', e.target.checked)}
              />
              Premium
            </label>
          </div>
        </div>

        <div className="pricing-section">
          <h3>Pricing</h3>
          <div className="price-inputs">
            <div className="price-input">
              <label htmlFor="pricingUSD">USD ($):</label>
              <input
                type="number"
                id="pricingUSD"
                step="0.01"
                value={pricingUSD}
                onChange={(e) => setPricingUSD(e.target.value)}
                placeholder="USD Price"
                disabled={!availability.includes('US')}
              />
            </div>
            <div className="price-input">
              <label htmlFor="pricingINR">INR (₹):</label>
              <input
                type="number"
                id="pricingINR"
                step="0.01"
                value={pricingINR}
                onChange={(e) => setPricingINR(e.target.value)}
                placeholder="INR Price"
                disabled={!availability.includes('IN')}
              />
            </div>
            <div className="price-input">
              <label htmlFor="pricingGBP">GBP (£):</label>
              <input
                type="number"
                id="pricingGBP"
                step="0.01"
                value={pricingGBP}
                onChange={(e) => setPricingGBP(e.target.value)}
                placeholder="GBP Price"
                disabled={!availability.includes('GB')}
              />
            </div>
            <div className="price-input">
              <label htmlFor="pricingEUR">EUR (€):</label>
              <input
                type="number"
                id="pricingEUR"
                step="0.01"
                value={pricingEUR}
                onChange={(e) => setPricingEUR(e.target.value)}
                placeholder="EUR Price"
                disabled={!availability.includes('EU')}
              />
            </div>
            <div className="price-input">
              <label htmlFor="pricingJPY">JPY (¥):</label>
              <input
                type="number"
                id="pricingJPY"
                step="0.01"
                value={pricingJPY}
                onChange={(e) => setPricingJPY(e.target.value)}
                placeholder="JPY Price"
                disabled={!availability.includes('JP')}
              />
            </div>
          </div>
        </div>
        
        <div className="stock-section">
          <h3>Stock by Country</h3>
          <div className="stock-inputs">
            <div className="stock-input">
              <label htmlFor="stockUS">US Stock:</label>
              <input
                type="number"
                id="stockUS"
                value={stockUS}
                onChange={(e) => setStockUS(e.target.value)}
                placeholder="US Stock"
                disabled={!availability.includes('US')}
              />
            </div>
            <div className="stock-input">
              <label htmlFor="stockIN">India Stock:</label>
              <input
                type="number"
                id="stockIN"
                value={stockIN}
                onChange={(e) => setStockIN(e.target.value)}
                placeholder="India Stock"
                disabled={!availability.includes('IN')}
              />
            </div>
            <div className="stock-input">
              <label htmlFor="stockGB">UK Stock:</label>
              <input
                type="number"
                id="stockGB"
                value={stockGB}
                onChange={(e) => setStockGB(e.target.value)}
                placeholder="UK Stock"
                disabled={!availability.includes('GB')}
              />
            </div>
            <div className="stock-input">
              <label htmlFor="stockEU">Europe Stock:</label>
              <input
                type="number"
                id="stockEU"
                value={stockEU}
                onChange={(e) => setStockEU(e.target.value)}
                placeholder="Europe Stock"
                disabled={!availability.includes('EU')}
              />
            </div>
            <div className="stock-input">
              <label htmlFor="stockJP">Japan Stock:</label>
              <input
                type="number"
                id="stockJP"
                value={stockJP}
                onChange={(e) => setStockJP(e.target.value)}
                placeholder="Japan Stock"
                disabled={!availability.includes('JP')}
              />
            </div>
          </div>
        </div>

        <div className="availability-section">
          <h3>Available In</h3>
          <div className="country-checkboxes">
            <label>
              <input
                type="checkbox"
                checked={availability.includes('US')}
                onChange={(e) => handleAvailabilityChange('US', e.target.checked)}
              />
              United States
            </label>
            <label>
              <input
                type="checkbox"
                checked={availability.includes('IN')}
                onChange={(e) => handleAvailabilityChange('IN', e.target.checked)}
              />
              India
            </label>
            <label>
              <input
                type="checkbox"
                checked={availability.includes('GB')}
                onChange={(e) => handleAvailabilityChange('GB', e.target.checked)}
              />
              United Kingdom
            </label>
            <label>
              <input
                type="checkbox"
                checked={availability.includes('EU')}
                onChange={(e) => handleAvailabilityChange('EU', e.target.checked)}
              />
              Europe
            </label>
            <label>
              <input
                type="checkbox"
                checked={availability.includes('JP')}
                onChange={(e) => handleAvailabilityChange('JP', e.target.checked)}
              />
              Japan
            </label>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="countryOfOrigin">Country of Origin:</label>
          <input
            type="text"
            id="countryOfOrigin"
            value={countryOfOrigin}
            onChange={(e) => setCountryOfOrigin(e.target.value)}
            placeholder="India"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="brandName">Brand Name:</label>
          <input
            type="text"
            id="brandName"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            placeholder="Organic"
          />
        </div>

        <div className="detailed-info-section">
          <h3>Detailed Product Information</h3>
          
          <div className="form-group">
            <label htmlFor="cardBenefit">Card Benefit (Line 1):</label>
            <input
              type="text"
              id="cardBenefit"
              value={cardBenefit}
              onChange={(e) => setCardBenefit(e.target.value)}
              placeholder="e.g., Digestive Aid"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="cardPackageInfo">Card Package Info (Line 2):</label>
            <input
              type="text"
              id="cardPackageInfo"
              value={cardPackageInfo}
              onChange={(e) => setCardPackageInfo(e.target.value)}
              placeholder="e.g., 12 or more Glass Bottles"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="cardKeyFeatures">Card Key Features (Line 3):</label>
            <input
              type="text"
              id="cardKeyFeatures"
              value={cardKeyFeatures}
              onChange={(e) => setCardKeyFeatures(e.target.value)}
              placeholder="e.g., 17g of Fresh Ginger | 20mg of Vitamin C"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="highlights">Product Highlights:</label>
            <textarea
              id="highlights"
              value={highlights}
              onChange={(e) => setHighlights(e.target.value)}
              placeholder="• 100% Organic ingredients\n• Cold-pressed for maximum potency\n• No artificial additives"
              rows={4}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="ingredients">Ingredients:</label>
            <textarea
              id="ingredients"
              value={ingredients}
              onChange={(e) => setIngredients(e.target.value)}
              placeholder="Organic ginger root juice, organic cayenne pepper, organic lemon juice, filtered water"
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="usageInstructions">Usage Instructions:</label>
            <textarea
              id="usageInstructions"
              value={usageInstructions}
              onChange={(e) => setUsageInstructions(e.target.value)}
              placeholder="Take 1 shot daily. Can be consumed straight or mixed with water. Best taken in the morning."
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="storageInstructions">Storage Instructions:</label>
            <textarea
              id="storageInstructions"
              value={storageInstructions}
              onChange={(e) => setStorageInstructions(e.target.value)}
              placeholder="Store in refrigerator. Consume within 3 days of opening. Keep away from direct sunlight."
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="certifications">Certifications & Standards:</label>
            <textarea
              id="certifications"
              value={certifications}
              onChange={(e) => setCertifications(e.target.value)}
              placeholder="USDA Organic Certified, Kosher, Gluten Free, Vegan, Lab Tested"
              rows={2}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="productHighlights">Product Highlights (Legacy):</label>
            <textarea
              id="productHighlights"
              value={productHighlights}
              onChange={(e) => setProductHighlights(e.target.value)}
              placeholder="Our wholesome organic product details, cultivation methods, farm to store policy..."
              rows={4}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="appearance">Appearance:</label>
            <textarea
              id="appearance"
              value={appearance}
              onChange={(e) => setAppearance(e.target.value)}
              placeholder="Describe physical appearance"
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="benefits">Benefits:</label>
            <textarea
              id="benefits"
              value={benefits}
              onChange={(e) => setBenefits(e.target.value)}
              placeholder="List key benefits"
              rows={4}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="shelfLife">Shelf Life:</label>
            <textarea
              id="shelfLife"
              value={shelfLife}
              onChange={(e) => setShelfLife(e.target.value)}
              placeholder="Storage and shelf life information"
              rows={3}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="disclaimer">Disclaimer:</label>
            <textarea
              id="disclaimer"
              value={disclaimer}
              onChange={(e) => setDisclaimer(e.target.value)}
              placeholder="Add any disclaimers"
              rows={4}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="productDesc">Basic Description:</label>
          <textarea
            id="productDesc"
            value={productDesc}
            onChange={(e) => setProductDesc(e.target.value)}
            placeholder="Enter product description"
            rows={2}
          />
        </div>

        <div className="form-group">
          <label htmlFor="productImage">Product Image URL:</label>
          <input
            type="text"
            id="productImage"
            placeholder="/image-name.png"
            value={productImage?.path || ''}
            onChange={(e) => setProductImage({ path: e.target.value })}
          />
          <small style={{color: '#666', marginTop: '5px', display: 'block'}}>Use format: /ImageName.png (images in public folder)</small>
        </div>

        <div className="form-group">
          <label htmlFor="videoUrl">Product Video URL (YouTube/Vimeo embed):</label>
          <input
            type="text"
            id="videoUrl"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/embed/VIDEO_ID"
          />
          <small style={{color: '#666', marginTop: '5px', display: 'block'}}>Use embed format: youtube.com/embed/VIDEO_ID (not youtube.com/watch?v=)</small>
        </div>

        <div className="gallery-section">
          <h3>Gallery Images (4-5 images)</h3>
          <p style={{fontSize: '12px', color: '#666', marginBottom: '10px'}}>Use format: /ImageName.png</p>
          <div className="gallery-grid">
            {[0, 1, 2, 3].map((idx) => (
              <div key={idx} className="gallery-upload">
                <label htmlFor={`gallery-${idx}`}>Image {idx + 1} URL</label>
                <input
                  type="text"
                  id={`gallery-${idx}`}
                  placeholder="/image-name.png"
                  value={galleryImages[idx]}
                  onChange={(e) => {
                    const newGallery = [...galleryImages];
                    newGallery[idx] = e.target.value;
                    setGalleryImages(newGallery);
                  }}
                />
                {galleryImages[idx] && (
                  <>
                    <img src={galleryImages[idx]} alt={`Gallery ${idx + 1}`} className="gallery-preview" onError={(e) => e.target.src = 'https://placehold.co/150x150?text=Not+Found'} />
                    <button
                      type="button"
                      onClick={() => {
                        const newGallery = [...galleryImages];
                        newGallery[idx] = "";
                        setGalleryImages(newGallery);
                      }}
                      className="clear-button"
                    >
                      Clear
                    </button>
                  </>
                )}
                {!galleryImages[idx] && <small>No URL entered</small>}
              </div>
            ))}
          </div>
        </div>

        <div className="form-group checkbox">
          <label htmlFor="isOrganic">
            <input
              type="checkbox"
              id="isOrganic"
              checked={isOrganic}
              onChange={(e) => setIsOrganic(e.target.checked)}
            />
            Is Organic Product
          </label>
        </div>

        <button onClick={addProduct} className="add-button">Add Product</button>
        
        <button 
          onClick={handleMigrateContent} 
          className="migrate-button"
          disabled={migrating}
          style={{
            marginTop: '10px',
            padding: '10px 20px',
            background: '#ff9800',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: migrating ? 'not-allowed' : 'pointer',
            opacity: migrating ? 0.6 : 1
          }}
        >
          {migrating ? 'Migrating...' : 'Migrate Existing Products'}
        </button>
        
        <div className="excel-import-section">
          <h3>Bulk Import from Excel</h3>
          <div className="form-group">
            <label htmlFor="excelFile">Select Excel File:</label>
            <input 
              type="file" 
              id="excelFile"
              accept=".xlsx,.xls,.csv" 
              onChange={handleExcelChange} 
            />
          </div>
          <div className="import-actions">
            <button 
              onClick={importFromExcel} 
              className="import-button"
              disabled={importing || !excelFile}
            >
              {importing ? 'Importing...' : 'Import Products'}
            </button>
            <a 
              href="/sample-products.csv" 
              download="sample-products.csv"
              className="download-template"
            >
              Download Sample Template
            </a>
          </div>
        </div>
      </div>

      <div className="product-list">
        <div className="product-list-header">
          <h1>Product List</h1>
          <div className="filter-controls">
            <label>
              <input 
                type="checkbox" 
                checked={showInactive} 
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show Inactive Products
            </label>
          </div>
        </div>
        
        {error && <div className="error-message">Error: {error}</div>}
        
        {products.length === 0 ? (
          <div>
            <p>No products found. Add some products above.</p>
            <button onClick={fetchProducts}>Refresh Products</button>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>US Price/Stock</th>
                <th>IN Price/Stock</th>
                <th>GB Price/Stock</th>
                <th>EU Price/Stock</th>
                <th>JP Price/Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product._id} className={product.active === false ? "inactive-product" : ""}>
                  <td>
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="product-image"
                        style={{ width: "50px", height: "50px", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = "https://placehold.co/50x50?text=Error";
                        }}
                      />
                    ) : (
                      "No Image"
                    )}

                    {product.is_organic === 1 && (
                      <Typography variant="caption" style={{ color: "green", fontWeight: "bold" }}>
                        🌿
                      </Typography>
                    )}
                  </td>
                  <td>{product.name}</td>
                  <td className={isAvailableIn(product, 'US') ? "" : "unavailable"}>
                    {isAvailableIn(product, 'US') ? (
                      <>
                        {formatPrice(product.pricing?.USD, 'US')} / {product.unit || 'Kg'}
                        <br />
                        <small>Stock: {getCountryStock(product, 'US')} {product.unit || 'Kg'}</small>
                      </>
                    ) : "N/A"}
                  </td>
                  <td className={isAvailableIn(product, 'IN') ? "" : "unavailable"}>
                    {isAvailableIn(product, 'IN') ? (
                      <>
                        {formatPrice(product.pricing?.INR, 'IN')} / {product.unit || 'kg'}
                        <br />
                        <small>Stock: {getCountryStock(product, 'IN')} {product.unit || 'kg'}</small>
                      </>
                    ) : "N/A"}
                  </td>
                  <td className={isAvailableIn(product, 'GB') ? "" : "unavailable"}>
                    {isAvailableIn(product, 'GB') ? (
                      <>
                        {formatPrice(product.pricing?.GBP, 'GB')} / {product.unit || 'kg'}
                        <br />
                        <small>Stock: {getCountryStock(product, 'GB')} {product.unit || 'kg'}</small>
                      </>
                    ) : "N/A"}
                  </td>
                  <td className={isAvailableIn(product, 'EU') ? "" : "unavailable"}>
                    {isAvailableIn(product, 'EU') ? (
                      <>
                        {formatPrice(product.pricing?.EUR, 'EU')} / {product.unit || 'kg'}
                        <br />
                        <small>Stock: {getCountryStock(product, 'EU')} {product.unit || 'kg'}</small>
                      </>
                    ) : "N/A"}
                  </td>
                  <td className={isAvailableIn(product, 'JP') ? "" : "unavailable"}>
                    {isAvailableIn(product, 'JP') ? (
                      <>
                        {formatPrice(product.pricing?.JPY, 'JP')} / {product.unit || 'kg'}
                        <br />
                        <small>Stock: {getCountryStock(product, 'JP')} {product.unit || 'kg'}</small>
                      </>
                    ) : "N/A"}
                  </td>
                  <td>
                    <span className={`status-badge ${product.active === false ? 'inactive' : 'active'}`}>
                      {product.active === false ? 'Inactive' : 'Active'}
                    </span>
                  </td>
                  <td className="action-buttons">
                    <button onClick={() => openProductDetails(product)}>Details</button>
                    {product.active === false ? (
                      <button onClick={() => restoreProduct(product._id)} className="restore-button">Restore</button>
                    ) : (
                      <button onClick={() => deleteProduct(product._id)} className="delete-button">Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProductList;